import { useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";

interface ListInputProps {
	id: string;
	onChange: (values: string[]) => void;
	/** With options, values are picked from a list instead of typed. */
	options?: { label: string; value: string }[];
	placeholder?: string;
	values: string[];
}

/** A removable list of strings, typed or picked from a list. */
export const ListInput = ({
	id,
	onChange,
	options,
	placeholder,
	values,
}: ListInputProps) => {
	const [entry, setEntry] = useState("");
	const add = () => {
		const value = entry.trim();
		if (value && !values.includes(value)) {
			onChange([...values, value]);
		}
		setEntry("");
	};
	const available = options?.filter((option) => !values.includes(option.value));

	return (
		<div className="flex flex-col gap-2">
			{values.length > 0 && (
				<ul className="flex flex-wrap gap-1">
					{values.map((value) => (
						<li
							className="flex items-center gap-1 rounded border border-secondary px-1.5 py-0.5 text-xs text-primary"
							key={value}
						>
							<span>{value}</span>
							<Button
								aria-label={`Remove ${value}`}
								color="link-gray"
								onClick={() => onChange(values.filter((v) => v !== value))}
								size="sm"
								type="button"
							>
								×
							</Button>
						</li>
					))}
				</ul>
			)}
			<div className="flex gap-2">
				{available ? (
					<NativeSelect
						id={id}
						onChange={(event) => setEntry(event.target.value)}
						options={[{ label: "Choose…", value: "" }, ...available]}
						value={entry}
					/>
				) : (
					<Input
						id={id}
						onChange={setEntry}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								add();
							}
						}}
						placeholder={placeholder}
						value={entry}
					/>
				)}
				<Button
					color="secondary"
					isDisabled={!entry.trim()}
					onClick={add}
					size="sm"
					type="button"
				>
					Add
				</Button>
			</div>
		</div>
	);
};
